import styled, { css } from 'styled-components';
import { shouldIgnoreProp } from '../../../../utils/shouldIgnoreProp';
import { BoxProps } from '../../../layout/Box';
import { Flex } from '../../../layout/Flex';
import { Typography } from '../../../typography/Typography';
import {
    DEFAULT_DESCRIPTION_HEIGHT,
    EXPANDER_BOTTOM_PADDING_EXPANDED,
    EXPANDER_PRODUCT_EXPANDED_Y_INDENTION,
    EXPANDER_DEFAULT_Y_INDENTION,
    SHORT_DESCRIPTION_PRODUCT_EXPANDED_WIDTH_INDENTION,
    SHORT_DESCRIPTION_WIDTH_INDENTION,
    EXPANDER_BOTTOM_EXPANDED_TEXT_PADDING_EXPANDED,
} from './constants';

interface DescriptionProps extends BoxProps {
    type: 'short' | 'normal';
    isExpanded: boolean;
    isProductExpanded: boolean;
}

export const Description = styled(Typography)
    .withConfig({
        displayName: 'Description',
        shouldForwardProp: shouldIgnoreProp('type', 'isExpanded', 'isProductExpanded'),
    })
    .attrs((props) => ({
        ...props,
        variant: 'body2',
        color: '#FFFFFF',
        textShadow: '0px 1px 2px rgba(0, 0, 0, 0.10)',
        textDecoration: 'none',
    }))<DescriptionProps>`
    ${({ type, isExpanded }: DescriptionProps) =>
        type === 'short'
            ? css`
                  white-space: nowrap;
                  overflow: hidden;
                  position: absolute;
                  text-overflow: ellipsis;
                  width: calc(100% - ${SHORT_DESCRIPTION_WIDTH_INDENTION}px);
                  opacity: ${isExpanded ? 0 : 1};
              `
            : css`
                  width: 100%;
                  overflow-y: auto;
                  opacity: ${isExpanded ? 1 : 0};
                  scrollbar-color: ${isExpanded
                      ? 'rgba(255, 255, 255, 0.65) transparent'
                      : 'transparent transparent'};
                  scrollbar-width: thin;
              `}

    ${({ type, isProductExpanded }) =>
        type === 'short' &&
        isProductExpanded &&
        css`
            width: calc(100% - ${SHORT_DESCRIPTION_PRODUCT_EXPANDED_WIDTH_INDENTION}px);
        `}
`;

export const Text = styled(Typography)
    .withConfig({ displayName: 'Text' })
    .attrs((props) => ({
        ...props,
        variant: 'caption',
        color: '#FFFFFF',
        textShadow: '0px 1px 2px rgba(0, 0, 0, 0.10)',
        textDecoration: 'none',
        overflow: 'hidden',
    }))`
    white-space: nowrap;
    text-overflow: ellipsis;
`;

interface ContentExpanderProps {
    isExpanded: boolean;
    isProductExpanded: boolean;
    descriptionHeight: number;
}

export const ContentExpander = styled(Flex)
    .withConfig({
        displayName: 'ContentExpander',
        shouldForwardProp: shouldIgnoreProp('isExpanded', 'isProductExpanded', 'descriptionHeight'),
    })
    .attrs((props) => ({
        ...props,
        justifyContent: 'space-between',
        pointerEvents: 'auto',
        cursor: 'pointer',
        px: 2,
        width: '100%',
    }))<ContentExpanderProps>`
    transform: ${({ isExpanded, isProductExpanded, descriptionHeight }) => {
        if (isProductExpanded && !isExpanded) {
            return `translateY(${descriptionHeight - EXPANDER_PRODUCT_EXPANDED_Y_INDENTION}px)`;
        }

        if (!isExpanded) {
            const currentY =
                (descriptionHeight || DEFAULT_DESCRIPTION_HEIGHT) - EXPANDER_DEFAULT_Y_INDENTION;

            return `translateY(${currentY}px)`;
        }
    }};

    padding-bottom: ${({ isExpanded, isProductExpanded }) => {
        if (isExpanded && isProductExpanded) {
            return `${EXPANDER_BOTTOM_EXPANDED_TEXT_PADDING_EXPANDED}px`;
        }

        if (isExpanded) {
            return `${EXPANDER_BOTTOM_PADDING_EXPANDED}px`;
        }
    }};
`;

export const Footer = styled(Flex)
    .withConfig({ displayName: 'Footer' })
    .attrs((props) => ({
        ...props,
        as: 'footer',
        position: 'absolute',
        left: 0,
        width: 'calc(100% - 40px)',
        overflow: 'hidden',
        alignItems: 'end',
        direction: 'row',
        minHeight: 'calc(100% + 66px)',
    }))``;
